// src/components/layout/Header.jsx
import React, { useContext } from 'react';
import {
    AppBar, Toolbar, Typography, Button, Box, Avatar, Menu, MenuItem,
    IconButton, useTheme, useMediaQuery
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import {
    AccountCircle, Security as SecurityIcon, ExitToApp,
    Menu as MenuIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';

const Header = ({ mobileOpen, setMobileOpen }) => {
    const { currentUser, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleClose();
        logout();
        navigate('/login');
    };

    const handleProfile = () => {
        handleClose();
        navigate('/profile');
    };

    const handleMobileMenuToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Show different layouts based on login status and screen size
    const isLoggedIn = Boolean(currentUser);

    return (
        <AppBar
            position="static"
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                ...(isLoggedIn && !isMobile && {
                    ml: '240px',
                    width: 'calc(100% - 240px)'
                })
            }}
        >
            <Toolbar>
                {/* Mobile menu button - only show when logged in and on mobile */}
                {isLoggedIn && isMobile && (
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleMobileMenuToggle}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}

                {/* Logo - responsive sizing */}
                <Typography
                    variant="h6"
                    component={Link}
                    to={isLoggedIn ? "/dashboard" : "/"}
                    sx={{
                        flexGrow: 1,
                        textDecoration: 'none',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem' }
                    }}
                >
                    <SecurityIcon sx={{
                        mr: 1,
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                        // Hide logo icon on very small screens when mobile menu is visible
                        ...(isLoggedIn && isMobile && { display: { xs: 'none', sm: 'block' } })
                    }} />
                    {/* Show abbreviated logo on very small mobile screens */}
                    <Box component="span" sx={{
                        display: { xs: isLoggedIn && isMobile ? 'none' : 'block', sm: 'block' }
                    }}>
                        RiskAS
                    </Box>
                    {/* Show just "Risk" on very small screens when mobile menu button is present */}
                    <Box component="span" sx={{
                        display: { xs: isLoggedIn && isMobile ? 'block' : 'none', sm: 'none' }
                    }}>
                        Risk
                    </Box>
                </Typography>

                {/* User menu or auth buttons */}
                {currentUser ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1 } }}>
                        {/* Welcome text - hide on very small screens */}
                        <Typography
                            variant="body2"
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                mr: 1,
                                color: 'rgba(255, 255, 255, 0.8)'
                            }}
                        >
                            Welcome, {currentUser.full_name?.split(' ')[0] || 'User'}
                        </Typography>

                        <IconButton
                            size="large"
                            onClick={handleMenu}
                            color="inherit"
                            aria-label="account menu"
                        >
                            <Avatar sx={{
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                bgcolor: 'secondary.main',
                                fontSize: { xs: '0.8rem', sm: '1rem' }
                            }}>
                                {currentUser.full_name ? currentUser.full_name.charAt(0) : 'U'}
                            </Avatar>
                        </IconButton>

                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            <MenuItem onClick={handleProfile} sx={{ display: 'none' }}>
                                <AccountCircle sx={{ mr: 1 }} />
                                Profile
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>
                                <ExitToApp sx={{ mr: 1 }} />
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        gap: { xs: 0.5, sm: 1 },
                        '& .MuiButton-root': {
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            px: { xs: 1, sm: 2 }
                        }
                    }}>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/login"
                            size={isMobile ? "small" : "medium"}
                            sx={{
                                color: 'white',
                                borderColor: 'white',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            Login
                        </Button>
                        <Button
                            variant="contained"
                            component={Link}
                            to="/register"
                            color="secondary"
                            size={isMobile ? "small" : "medium"}
                        >
                            Register
                        </Button>
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default Header;